// Quality indicator values in words (docs/plans/phase3-khis-and-quality.md §1.2). Every
// number on the dashboard is written out; nothing rests on colour.
import { QUALITY_INDICATORS, type QualityIndicatorKey } from '@wodi/domain';
import type { QualityIndicatorView, QualityMonthView } from '@wodi/shared';

import { formatWaiting } from '../format/waiting.ts';

const UNIT_LABEL: Record<(typeof QUALITY_INDICATORS)[QualityIndicatorKey]['unit'], string> = {
  minutes: 'Median',
  days: 'Median days',
  percent: 'Percent',
  count: 'Items',
};

/** The table column heading for an indicator's headline value. */
export function valueLabel(key: QualityIndicatorKey): string {
  return UNIT_LABEL[QUALITY_INDICATORS[key].unit];
}

/** `30 min`, `2.5 days`, `12.5%`, `3 items`, or `No data`. */
export function valueWords(key: QualityIndicatorKey, value: number | null): string {
  if (value === null) return 'No data';
  switch (QUALITY_INDICATORS[key].unit) {
    case 'minutes':
      return formatWaiting(Math.round(value));
    case 'days':
      return `${String(value)} ${value === 1 ? 'day' : 'days'}`;
    case 'percent':
      return `${String(value)}%`;
    case 'count':
      return `${String(value)} ${value === 1 ? 'item' : 'items'}`;
  }
}

const COUNTED: Record<QualityIndicatorKey, [string, string]> = {
  wait_to_consultation: ['visit', 'visits'],
  length_of_stay: ['discharge', 'discharges'],
  bed_occupancy: ['bed', 'beds'],
  lab_turnaround: ['lab request', 'lab requests'],
  critical_ack_time: ['critical result', 'critical results'],
  claim_rejection_rate: ['claim decided', 'claims decided'],
  stock_outs: ['item', 'items'],
  readmissions_30d: ['discharge alive', 'discharges alive'],
};

function count(key: QualityIndicatorKey, n: number): string {
  const [one, many] = COUNTED[key];
  return `${n.toLocaleString('en-KE')} ${n === 1 ? one : many}`;
}

/** One sentence for a month: the headline, what it was measured on, and its details. */
export function monthSentence(key: QualityIndicatorKey, m: QualityMonthView): string {
  if (key === 'stock_outs')
    return m.value === 0
      ? 'No item with a reorder level is out of stock now.'
      : `${valueWords(key, m.value)} with a reorder level ${m.value === 1 ? 'has' : 'have'} no usable stock now.`;
  if (m.value === null) return `Nothing to measure (${count(key, m.sampleSize)}).`;
  const parts = [`${valueLabel(key)} ${valueWords(key, m.value)}`];
  if (m.p90 !== null) parts.push(`9 in 10 within ${valueWords(key, m.p90)}`);
  if (m.meanDays !== null) parts.push(`mean ${valueWords(key, m.meanDays)}`);
  if (m.withinTargetPercent !== null)
    parts.push(`${String(m.withinTargetPercent)}% within the test's own target time`);
  if (m.notAcknowledged !== null && m.notAcknowledged > 0)
    parts.push(`${String(m.notAcknowledged)} not acknowledged yet`);
  if (m.closedByLab !== null && m.closedByLab > 0)
    parts.push(`${String(m.closedByLab)} closed by the lab after phoning`);
  const sentence = `${parts.join(', ')}; ${count(key, m.sampleSize)}.`;
  return m.stillCounting
    ? `${sentence} Still counting: some discharges' 30 days have not passed.`
    : sentence;
}

/** "Target 30 min. Met." in words, or "No target set." */
export function targetSentence(i: QualityIndicatorView): string {
  if (i.target === null) return 'No target set.';
  const target = `Target ${valueWords(i.key, i.target)}${QUALITY_INDICATORS[i.key].better === 'lower' ? ' or less' : ' or more'}.`;
  switch (i.targetStatus) {
    case 'met':
      return `${target} Met.`;
    case 'not_met':
      return `${target} Not met.`;
    default:
      return `${target} Nothing to compare yet.`;
  }
}
